import { useQuery } from "@tanstack/react-query";
import { xano, type VatPackResponse, type ThresholdRadarResponse } from "@/lib/xano";
import { useCompany } from "@/lib/company";
import { SectionTitle, DataTable } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { gbp, num } from "@/lib/format";

const statusVariant = (s: string): "secondary" | "success" | "warning" | "destructive" => {
  if (s === "registered") return "success";
  if (s === "over") return "destructive";
  if (s === "approaching") return "warning";
  return "secondary";
};

function buildCsv(d: VatPackResponse): string {
  const p = d.pack;
  const lines: string[] = [];
  lines.push(`GoEcom VAT pack,${d.company_name}`);
  lines.push(`Generated,${d.generated_at}`);
  lines.push("");
  lines.push("UK VAT return,Amount (GBP)");
  lines.push(`Box 1 - VAT due on sales,${p.boxes.box1_vat_due}`);
  lines.push(`Box 6 - Total sales ex VAT,${p.boxes.box6_total_sales}`);
  lines.push(`Zero-rated exports (net),${p.zero_rated_exports.net}`);
  lines.push(`IOSS VAT to declare (total),${p.ioss_vat_total}`);
  lines.push("");
  lines.push("IOSS VAT by country,Country,Orders,Net (GBP),VAT due (GBP)");
  p.ioss_by_country.forEach((c) => lines.push(`,${c.country},${c.orders},${c.net},${c.vat_due}`));
  lines.push("");
  lines.push("VAT treatment,Orders,Net (GBP),VAT (GBP)");
  p.treatments.forEach((t) => lines.push(`${t.label},${t.orders},${t.net},${t.vat}`));
  lines.push("");
  lines.push("Corrections needed,Order,Country,Treatment,Issue");
  p.corrections.forEach((c) =>
    lines.push(`,${c.order},${c.country},${c.treatment},"${c.issue.replace(/"/g, "'")}"`)
  );
  return lines.join("\n");
}

function downloadCsv(d: VatPackResponse) {
  const blob = new Blob([buildCsv(d)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `goecom-vat-pack-${d.company_name.replace(/\s+/g, "-").toLowerCase()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function VatToolsSection() {
  const { activeToken } = useCompany();

  const radar = useQuery({
    queryKey: ["threshold-radar", activeToken],
    queryFn: () => xano.thresholdRadar(activeToken!),
    enabled: !!activeToken,
  });
  const pack = useQuery({
    queryKey: ["vat-pack", activeToken],
    queryFn: () => xano.vatPack(activeToken!),
    enabled: !!activeToken,
  });

  const r = radar.data as ThresholdRadarResponse | undefined;
  const d = pack.data as VatPackResponse | undefined;

  return (
    <>
      {/* Threshold radar */}
      <SectionTitle>Threshold radar</SectionTitle>
      <p className="-mt-2 mb-3 text-sm text-muted-foreground">
        Your annualised run-rate against the VAT thresholds that change your obligations.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        {(r?.radar ?? []).map((t) => (
          <Card key={t.key}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="font-medium">{t.label}</div>
                <Badge variant={statusVariant(t.status)}>{t.status}</Badge>
              </div>
              <div className="mt-2 text-sm text-muted-foreground">{t.threshold_label}</div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-secondary">
                <div
                  className={
                    "h-full rounded-full " +
                    (t.status === "over" ? "bg-destructive" : t.status === "approaching" ? "bg-amber-500" : "bg-primary")
                  }
                  style={{ width: `${Math.min(100, t.pct_of_threshold)}%` }}
                />
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="tabular-nums">
                  {gbp(t.current_annualised)} <span className="text-muted-foreground">/ yr</span>
                </span>
                <span className="text-muted-foreground">{t.pct_of_threshold}% of threshold</span>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">{t.note}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Manual VAT pack */}
      <div className="mt-8 flex items-center justify-between">
        <SectionTitle>VAT pack</SectionTitle>
        {d && (
          <Button variant="secondary" onClick={() => downloadCsv(d)}>
            <Download className="h-4 w-4" /> Download CSV
          </Button>
        )}
      </div>
      <p className="-mt-2 mb-3 text-sm text-muted-foreground">
        Return-ready figures and the IOSS VAT to declare by country. For filing yourself, or hand it
        to goforma.
      </p>
      {d && (
        <>
          <Card className="mb-4">
            <CardContent className="grid grid-cols-2 gap-4 p-5 sm:grid-cols-4">
              <Mini label="Box 1 - VAT due" value={gbp(d.pack.boxes.box1_vat_due, { decimals: true })} />
              <Mini label="Box 6 - Total sales" value={gbp(d.pack.boxes.box6_total_sales)} />
              <Mini label="Zero-rated exports" value={gbp(d.pack.zero_rated_exports.net)} />
              <Mini label="IOSS VAT to declare" value={gbp(d.pack.ioss_vat_total, { decimals: true })} />
            </CardContent>
          </Card>

          <div className="mb-2 text-sm font-medium">IOSS VAT by destination</div>
          <DataTable
            columns={[
              { key: "country", label: "Country" },
              { key: "orders", label: "Orders", align: "right" },
              { key: "net", label: "Net", align: "right" },
              { key: "vat", label: "VAT to declare", align: "right" },
            ]}
            rows={d.pack.ioss_by_country.map((c) => ({
              country: c.country,
              orders: num(c.orders),
              net: gbp(c.net, { decimals: true }),
              vat: <span className="font-medium">{gbp(c.vat_due, { decimals: true })}</span>,
            }))}
          />

          {d.pack.corrections.length > 0 && (
            <Card className="mt-4 border-destructive/30 bg-destructive/5">
              <CardContent className="p-5 text-sm">
                <div className="font-medium text-destructive">
                  {num(d.pack.corrections.length)} correction journals needed
                </div>
                <p className="mt-1 text-muted-foreground">
                  These orders were taxed incorrectly. The pack CSV lists each one; fix them in Books
                  before filing.
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-semibold tabular-nums">{value}</div>
    </div>
  );
}
